import { useEffect, useId, useRef, useState } from "react";
import type { InputHTMLAttributes } from "react";
import { formatDate, parseTypedDate } from "../utils/dateFormat";
import CalendarPopover from "./CalendarPopover";

/**
 * The one typed-date box (D109.6). Staff type dd/mm/yyyy; a native
 * <input type="date"> follows the browser locale, so on an en-US laptop typed
 * 20/10/2026 landed as 01/02/2026.
 *
 * Contract is the native input's, minus the event: `value` is ISO yyyy-mm-dd
 * (or ''), `onChange` gets ISO yyyy-mm-dd (or ''). It emits when the text is a
 * complete real date (or cleared), never for an impossible one, which shows an
 * inline error instead. On blur the box shows the parsed date back as
 * dd/mm/yyyy. `min` / `max` (ISO) are enforced the same way: out of range is
 * refused, not emitted.
 *
 * A refused date is NOT emitted, so the parent still holds the previous date.
 * `validity` is REQUIRED so a new saving form cannot forget it: pass a handler
 * and it is told false while the box holds text that is not a usable date (the
 * form must then refuse the save), or pass "filter" (a search/report control, the
 * value is never stored) / "immediate" (the host saves each emitted date at once,
 * so a refused one is simply never saved). The box never reports valid on
 * unmount: a hidden-but-refused box must keep blocking the save.
 * `commit="blur"` emits only on blur / Enter instead of on every keystroke that
 * forms a date, for boxes that save on change.
 *
 * Focusing or clicking the box opens a month calendar under it; a picked day
 * goes through the same commit as a typed one (range check, blur-commit, emit).
 */
type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "min" | "max"> & {
  value: string | null | undefined;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  /** Handler called whenever the box flips between usable (true) and refused/incomplete (false), or an explicit "nothing to block". */
  validity: ((valid: boolean) => void) | "filter" | "immediate";
  /** "change" (default) emits as soon as the text is a real date; "blur" only on blur / Enter. */
  commit?: "change" | "blur";
};

function seed(v: string | null | undefined): string {
  if (!v) return "";
  return formatDate(v) || v;
}

export default function DateInput({ value, onChange, min, max, validity, commit: commitMode = "change", onBlur, onKeyDown, onFocus, onClick, className, placeholder, ...rest }: Props) {
  const [text, setText] = useState(() => seed(value));
  const [err, setErr] = useState<string | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const canPick = !rest.disabled && !rest.readOnly;
  // The last ISO this box itself reported, so the parent echoing it back is not
  // mistaken for an outside change that should rewrite what is being typed.
  const lastEmitted = useRef<string>(value || "");

  const validRef = useRef(true);
  const validityCb = useRef(typeof validity === "function" ? validity : undefined);
  validityCb.current = typeof validity === "function" ? validity : undefined;
  const errId = useId();
  function setValid(v: boolean) {
    if (validRef.current === v) return;
    validRef.current = v;
    validityCb.current?.(v);
  }
  // A fresh box is seeded from the parent's value, so it starts usable: tell the
  // host, which may still hold "refused" from a box that was closed while refused.
  // Nothing is reported on unmount (a refused box that is merely hidden must keep
  // blocking the save).
  useEffect(() => {
    validityCb.current?.(true);
  }, []);

  useEffect(() => {
    if ((value || "") !== lastEmitted.current) {
      lastEmitted.current = value || "";
      setText(seed(value));
      setErr(null);
      setValid(true);
    }
  }, [value]);

  function rangeProblem(iso: string): string | null {
    if (min && iso < min) return `Date must be on or after ${formatDate(min)}`;
    if (max && iso > max) return `Date must be on or before ${formatDate(max)}`;
    return null;
  }

  // Returns true when the text is acceptable (empty, or a real in-range day).
  function commit(raw: string, final: boolean): boolean {
    const trimmed = raw.trim();
    if (!trimmed) {
      setErr(null);
      setValid(true);
      if (lastEmitted.current !== "" && (final || commitMode === "change")) {
        lastEmitted.current = "";
        onChange("");
      }
      return true;
    }
    const iso = parseTypedDate(trimmed);
    if (!iso) {
      // Half-typed text is not an error until the person leaves the box.
      if (final) setErr(`"${trimmed}" is not a valid date, type dd/mm/yyyy`);
      setValid(false);
      return false;
    }
    const problem = rangeProblem(iso);
    if (problem) {
      setErr(problem);
      setValid(false);
      return false;
    }
    setErr(null);
    setValid(true);
    if (iso !== lastEmitted.current && (final || commitMode === "change")) {
      lastEmitted.current = iso;
      onChange(iso);
    }
    if (final) setText(formatDate(iso));
    return true;
  }

  return (
    <>
      <input
        {...rest}
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder ?? "dd/mm/yyyy"}
        className={className}
        value={text}
        aria-invalid={err || rest["aria-invalid"] ? true : undefined}
        aria-describedby={[rest["aria-describedby"], err ? errId : undefined].filter(Boolean).join(" ") || undefined}
        onChange={(e) => {
          setText(e.target.value);
          commit(e.target.value, false);
        }}
        onFocus={(e) => {
          if (canPick) setCalendarOpen(true);
          onFocus?.(e);
        }}
        onClick={(e) => {
          if (canPick) setCalendarOpen(true);
          onClick?.(e);
        }}
        onBlur={(e) => {
          setCalendarOpen(false);
          commit(e.target.value, true);
          onBlur?.(e);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            setCalendarOpen(false);
            commit((e.target as HTMLInputElement).value, true);
          }
          onKeyDown?.(e);
        }}
      />
      {calendarOpen && canPick && (
        <CalendarPopover
          anchor={inputRef.current}
          value={parseTypedDate(text) || value}
          min={min}
          max={max}
          onClose={() => setCalendarOpen(false)}
          onPick={(iso) => {
            setCalendarOpen(false);
            const shown = formatDate(iso);
            setText(shown);
            commit(shown, true);
          }}
        />
      )}
      {err && (
        <span id={errId} role="alert" className="block text-xs text-red-600">
          {err}
        </span>
      )}
    </>
  );
}
